import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeleteRole, useSaveRole } from '../../mutations';
import { usePermissionCatalog, useRoles } from '../../queries';
import type { PermissionView, RoleView } from '../../types';
import { errorMessage } from '@/shared/api/problem';
import { Button } from '@/shared/ui/atoms/Button';
import { Checkbox } from '@/shared/ui/atoms/Checkbox';
import { FontIcon } from '@/shared/ui/atoms/FontIcon';
import { Input } from '@/shared/ui/atoms/Input';
import { Spinner } from '@/shared/ui/atoms/Spinner';
import { FormField } from '@/shared/ui/molecules/FormField';
import { Modal } from '@/shared/ui/organisms/Modal';
import { useToast } from '@/shared/ui/organisms/Toast/useToast';
import '../../../tasks/ui/NewTaskForm/NewTaskForm.styl';
import './RolesManager.styl';

type Editing = RoleView | 'new' | null;

/** The organisation's roles and, per module, the permissions each one grants. */
export function RolesManager() {
  const { t } = useTranslation(undefined, { keyPrefix: 'roleManager' });
  const roles = useRoles();
  const catalog = usePermissionCatalog();
  const remove = useDeleteRole();
  const toast = useToast();
  const [editing, setEditing] = useState<Editing>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  if (roles.isPending || catalog.isPending) return <Spinner label={t('loading')} />;

  return (
    <div className="f-roles">
      <div className="f-roles__bar">
        <p className="f-roles__hint">{t('intro')}</p>
        <Button variant="add" size="sm" iconLeft={<FontIcon name="plus-circle" />} onClick={() => setEditing('new')}>
          {t('new')}
        </Button>
      </div>
      <ul className="f-roles__list">
        {(roles.data ?? []).map((r) => (
          <li key={r.id} className="f-roles__item">
            <div className="f-roles__main">
              <div className="f-roles__name">
                {r.name}
                <span className={`f-roles__kind f-roles__kind--${r.kind.toLowerCase()}`}>{t(`kind.${r.kind}`)}</span>
              </div>
              {r.description ? <div className="f-roles__desc">{r.description}</div> : null}
              <div className="f-roles__meta">
                {t('permissionsCount', { count: r.permissions.length })} · {t('membersCount', { count: r.members })}
              </div>
            </div>
            <div className="f-roles__actions">
              <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                {r.editable ? t('edit') : t('view')}
              </Button>
              {r.deletable ? (
                confirming === r.id ? (
                  <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate(r.id, { onSuccess: () => toast.show(t('deleted'), 'success'), onError: (err) => toast.show(errorMessage(err, t('deleteError')), 'error'), onSettled: () => setConfirming(null) })}>
                    {t('confirmDelete')}
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" iconLeft={<FontIcon name="bin" />} onClick={() => setConfirming(r.id)}>
                    {t('delete')}
                  </Button>
                )
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? t('newTitle') : editing && !editing.editable ? editing.name : t('editTitle')} width={760}>
        {editing !== null ? (
          <RoleEditor
            role={editing === 'new' ? null : editing}
            catalog={catalog.data ?? []}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              toast.show(t('saved'), 'success');
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
}

function RoleEditor({ role, catalog, onSaved, onCancel }: { role: RoleView | null; catalog: PermissionView[]; onSaved: () => void; onCancel: () => void }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'roleManager' });
  const save = useSaveRole();
  const readOnly = !!role && !role.editable;
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [granted, setGranted] = useState<Set<string>>(() => new Set(role?.permissions ?? []));
  const [error, setError] = useState<string | null>(null);
  const modules = useMemo(() => {
    const byModule = new Map<string, PermissionView[]>();
    catalog.forEach((p) => byModule.set(p.module, [...(byModule.get(p.module) ?? []), p]));
    return [...byModule.entries()];
  }, [catalog]);

  const toggle = (code: string) => {
    const next = new Set(granted);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setGranted(next);
  };
  const toggleModule = (perms: PermissionView[]) => {
    const all = perms.every((p) => granted.has(p.code));
    const next = new Set(granted);
    perms.forEach((p) => (all ? next.delete(p.code) : next.add(p.code)));
    setGranted(next);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 2) return setError(t('nameRequired'));
    setError(null);
    save.mutate(
      { ...(role ? { id: role.id, version: role.version } : {}), name: name.trim(), description: description.trim() || null, permissions: [...granted] },
      { onSuccess: onSaved, onError: (err) => setError(errorMessage(err, t('saveError'))) }
    );
  };

  return (
    <form className="f-newtask" onSubmit={submit} noValidate>
      {readOnly ? <p className="f-newtask__note">{t('adminLocked')}</p> : null}
      <div className="f-newtask__row">
        <FormField label={t('name')} htmlFor="role-name">
          <Input icon="key2" id="role-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} disabled={readOnly} />
        </FormField>
        <FormField label={t('description')} htmlFor="role-desc">
          <Input icon="paragraph-left" id="role-desc" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} disabled={readOnly} />
        </FormField>
      </div>
      <div className="f-roles__matrix">
        {modules.map(([module, perms]) => {
          const all = perms.every((p) => granted.has(p.code));
          return (
            <fieldset key={module} className="f-roles__module">
              <legend className="f-roles__module-head">
                <span>{t(`modules.${module}` as never)}</span>
                {readOnly ? null : (
                  <button type="button" className="f-roles__all" onClick={() => toggleModule(perms)}>
                    {all ? t('none') : t('all')}
                  </button>
                )}
              </legend>
              {perms.map((p) => (
                <div key={p.code} className="f-roles__perm">
                  <Checkbox checked={granted.has(p.code)} onChange={() => toggle(p.code)} label={t(`permissions.${p.code.replace('.', '_')}` as never)} disabled={readOnly} />
                  <span className="f-roles__perm-hint">{t(`hints.${p.code.replace('.', '_')}` as never)}</span>
                </div>
              ))}
            </fieldset>
          );
        })}
      </div>
      {error ? (
        <div className="f-newtask__error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="f-newtask__actions">
        <Button variant="cancel" onClick={onCancel}>
          {readOnly ? t('close') : t('cancel')}
        </Button>
        {readOnly ? null : (
          <Button type="submit" loading={save.isPending} iconLeft={<FontIcon name="floppy-disk" />}>
            {t('save')}
          </Button>
        )}
      </div>
    </form>
  );
}
